import { useEffect, useState, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useAccess } from './useAccessScreen';
import { useWakeLock } from "./useWakeLock";
import { useDataAuth } from './useDataAuth';
import { callApiFlow } from '../services/api.service';

const TIMEREXECUTION = 1 * 60 * 1000;
export function useLocationRequest(setErrorMessage, isPWA) {
    const { requestWakeLock, releaseWakeLock } = useWakeLock();
    const { access, loadingAccess } = useAccess("HOME");
    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();
    const [blocked, setBlocked] = useState(false);
    const [hasRequested, setHasRequested] = useState(false);
    const isFollowUp = Boolean(access?.requestLocationFollowUp);

    const handleSave = useCallback(async (data) => {
        callApiFlow({ ...data, id_flow: "API-SAVE_LOCATION" }, userSession.token)
            .then(() => console.info("Saved location"))
            .catch((e) => console.error("ERROR_handleSave_location", e));
    }, [userSession.token]);

    const getLocation = useCallback((onSuccess, onError) => {
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                const c = {
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                    type: access?.requestLocationFollowUp || access?.requestLocation || "unknown"
                };
                onSuccess(c);
            },
            onError,
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            }
        );
    }, [access]);

    const requestLocation = useCallback(async () => {
        if (!isPWA || (!access?.requestLocation && !isFollowUp)) return;
        if (!("geolocation" in navigator)) {
            setBlocked(true);
            setErrorMessage(t('messages.supportGeolocalocation'));
            return;
        }
        getLocation(
            (c) => {
                setBlocked(false);
                setHasRequested(true);
                handleSave(c);
            },
            (err) => {
                setHasRequested(true);
                if (err.code === 1) {
                    setBlocked(true);
                    setErrorMessage(t('messages.mustGrantLocation'));
                } else {
                    setErrorMessage(t('messages.locationCouldNotBeObtained'));
                }
            }
        );
    }, [access, isFollowUp, getLocation, handleSave, setErrorMessage]);

    // FIRST SAVED
    useEffect(() => {
        if (loadingAccess) return;
        if (hasRequested) return;
        if (!access?.requestLocation && !isFollowUp) return;
        requestLocation();
    }, [loadingAccess, access, isFollowUp, hasRequested, requestLocation]);

    // FOLLOW UP EVERY 10 MINUTOS
    useEffect(() => {
        if (!isFollowUp) return;
        if (!hasRequested) return;
        // console.log("Active follow up every 10 minutes");
        const interval = setInterval(() => {
            getLocation(
                (c) => handleSave(c),
                (err) => console.warn("Error seguimiento:", err)
            );
        }, TIMEREXECUTION);
        return () => clearInterval(interval);
    }, [isFollowUp, hasRequested, getLocation, handleSave]);

    useEffect(() => {
        const shouldTrackLocation = isPWA && isFollowUp && hasRequested;
        if (shouldTrackLocation) {  
            requestWakeLock();
        }
        return () => releaseWakeLock();
    }, [isPWA, isFollowUp, hasRequested]);

    return {
        blocked,
        hasRequested,
        retry: requestLocation,
    };
}
